import { gql, useApolloClient } from "@apollo/client";
import { useMemo, useRef } from "react";
import { SuggestionOptions } from "@tiptap/suggestion";

import {
  PostMentionableUsersDocument,
  PostMentionableUsersQuery,
} from "./usePostMentionItems.generated";

const MENTION_CANDIDATES_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 250;

type MentionItems = NonNullable<SuggestionOptions["items"]>;

const usePostMentionItems = (): MentionItems => {
  const client = useApolloClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useMemo<MentionItems>(
    () =>
      ({ query }) =>
        new Promise((resolve) => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            client
              .query<PostMentionableUsersQuery>({
                query: PostMentionableUsersDocument,
                variables: {
                  search: query || undefined,
                  pagination: { limit: MENTION_CANDIDATES_LIMIT, offset: 0 },
                },
              })
              .then(({ data }) =>
                resolve(data?.PostMentionableUsers.objects ?? []),
              );
          }, SEARCH_DEBOUNCE_MS);
        }),
    [client],
  );
};

gql`
  query PostMentionableUsers($search: String, $pagination: PaginationInput) {
    PostMentionableUsers(search: $search, pagination: $pagination) {
      objects {
        id
        name
        firstName
        lastName
        profileImage {
          ...AvatarProfileImage
        }
      }
    }
  }
`;

export default usePostMentionItems;
